import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronDown, Edit2, Save } from 'lucide-react';
import AdminLayout from '@/components/layout/AdminLayout';
import apiService from '@/services/api';
import PageBuilder from '@/components/PageBuilder';

interface Section {
  id: number;
  page_slug: string;
  section_key: string;
  section_type: string;
  title?: string;
  subtitle?: string;
  body: string;
  display_order: number;
  is_active: boolean;
}

const AdminPageContentEditor: React.FC = () => {
  const { pageSlug = 'home' } = useParams();
  const navigate = useNavigate();
  const [sections, setSections] = useState<Section[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [editingSection, setEditingSection] = useState<Section | null>(null);

  useEffect(() => {
    fetchPageContent();
  }, [pageSlug]);

  const fetchPageContent = async () => {
    try {
      setLoading(true);
      const response = await apiService.request(`/public/page-sections/${pageSlug}`);
      if (response.success && Array.isArray(response.data)) {
        setSections(response.data.sort((a, b) => a.display_order - b.display_order));
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load page content');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSection = async (sectionId: number, data: any) => {
    try {
      // Validate and parse body if JSON
      let body = data.body;
      try {
        if (typeof body === 'string' && body.trim().startsWith('[')) {
          JSON.parse(body); // Validate JSON
        }
      } catch (e) {
        alert('Invalid JSON format in body field');
        return;
      }

      const response = await apiService.request(
        `/admin/page-sections/${sectionId}`,
        'PUT',
        {
          title: data.title,
          subtitle: data.subtitle,
          body: body,
          is_active: true
        }
      );

      if (response.success) {
        alert('✓ Section updated');
        setEditingSection(null);
        fetchPageContent();
      } else {
        alert('Error: ' + response.message);
      }
    } catch (err: any) {
      alert('Failed to save: ' + err.message);
    }
  };

  const pageNames: Record<string, string> = {
    home: 'Home Page',
    'how-it-works': 'How It Works',
    homeowners: 'For Homeowners',
    landlords: 'For Landlords'
  };

  return (
    <AdminLayout>
      <div className="flex-1 overflow-auto">
        <Helmet>
          <title>Edit Page Content | Virewire Admin</title>
        </Helmet>

        <div className="p-8 max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate('/admin/website-pages')}
              className="text-teal-600 hover:text-teal-700 text-sm font-medium mb-2"
            >
              ← Back to Pages
            </button>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {pageNames[pageSlug || 'home']}
            </h1>
            <p className="text-gray-600">Edit all sections including images, text, and layout</p>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="text-center py-12">
              <p className="text-gray-600">Loading page sections...</p>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-red-700">❌ {error}</p>
            </div>
          )}

          {/* Sections List */}
          {!loading && sections.length > 0 && (
            <div className="grid grid-cols-1 gap-4">
              {sections.map((section) => (
                <div
                  key={section.id}
                  className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">
                        {section.section_key}
                      </h3>
                      <p className="text-sm text-gray-600 mb-3">
                        Type: <span className="font-medium">{section.section_type}</span>
                      </p>
                      {section.title && (
                        <p className="text-sm text-gray-700 truncate">
                          <span className="text-gray-600">Title: </span>
                          {section.title}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => setEditingSection(section)}
                      className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors flex items-center gap-2 whitespace-nowrap"
                    >
                      <Edit2 className="w-4 h-4" />
                      Edit Content
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loading && sections.length === 0 && (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <p className="text-gray-600">No sections found for this page</p>
            </div>
          )}
        </div>

        {/* Page Builder Modal */}
        {editingSection && (
          <PageBuilder
            section={editingSection}
            onSave={handleSaveSection}
            onCancel={() => setEditingSection(null)}
          />
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminPageContentEditor;
